// Cache duration constants
export const CACHE_DURATIONS = {
  CONSULTATION_DETAILS: 5 * 60 * 1000, // 5 minutes
  PROFILE_DATA: 10 * 60 * 1000, // 10 minutes
  LOCATION_DATA: 10 * 60 * 1000, // 10 minutes
  SKILLS: 30 * 60 * 1000, // 30 minutes
  CATEGORIES: 30 * 60 * 1000, // 30 minutes
  COUNTRIES: 60 * 60 * 1000, // 1 hour
  STATES: 60 * 60 * 1000, // 1 hour
  CITIES: 60 * 60 * 1000, // 1 hour
} as const;

// Cache utility functions
export const isCacheValid = (lastFetched: number, duration: number): boolean => {
  return (Date.now() - lastFetched) < duration;
};

export const createLocationKey = (countryId: string, stateId: string, cityId: string = ''): string => {
  return `${countryId}-${stateId}-${cityId}`;
};

export const createConsultationKey = (consultationId: string): string => {
  return consultationId;
};

export const createProfileKey = (userId: string): string => {
  return userId;
};

// Cache validation helpers
export const isConsultationCacheValid = (lastFetched: number): boolean => {
  return isCacheValid(lastFetched, CACHE_DURATIONS.CONSULTATION_DETAILS);
};

export const isProfileCacheValid = (lastFetched: number): boolean => {
  return isCacheValid(lastFetched, CACHE_DURATIONS.PROFILE_DATA);
};

export const isLocationCacheValid = (lastFetched: number): boolean => {
  return isCacheValid(lastFetched, CACHE_DURATIONS.LOCATION_DATA);
};

export const isSkillsCacheValid = (lastFetched: number): boolean => {
  return isCacheValid(lastFetched, CACHE_DURATIONS.SKILLS);
};

export const isCategoriesCacheValid = (lastFetched: number): boolean => {
  return isCacheValid(lastFetched, CACHE_DURATIONS.CATEGORIES);
};

// Cache cleanup utilities
export const cleanupExpiredCache = (cacheObject: Record<string, any>, duration: number): Record<string, any> => {
  // Handle null/undefined cache objects
  if (!cacheObject || typeof cacheObject !== 'object') {
    return {};
  }
  
  const now = Date.now();
  const cleanedCache: Record<string, any> = {};
  
  Object.entries(cacheObject).forEach(([key, value]) => {
    if (value && value.lastFetched && isCacheValid(value.lastFetched, duration)) {
      cleanedCache[key] = value;
    }
  });
  
  return cleanedCache;
};

export const cleanupConsultationCache = (cacheObject: Record<string, any>): Record<string, any> => {
  return cleanupExpiredCache(cacheObject, CACHE_DURATIONS.CONSULTATION_DETAILS);
};

export const cleanupProfileCache = (cacheObject: Record<string, any>): Record<string, any> => {
  return cleanupExpiredCache(cacheObject, CACHE_DURATIONS.PROFILE_DATA);
};

export const cleanupLocationCache = (cacheObject: Record<string, any>): Record<string, any> => {
  return cleanupExpiredCache(cacheObject, CACHE_DURATIONS.LOCATION_DATA);
};

// Cache size management
export const getCacheSize = (cacheObject: Record<string, any>): number => {
  if (!cacheObject || typeof cacheObject !== 'object') {
    return 0;
  }
  return Object.keys(cacheObject).length;
};

export const limitCacheSize = (cacheObject: Record<string, any>, maxSize: number): Record<string, any> => {
  // Handle null/undefined cache objects
  if (!cacheObject || typeof cacheObject !== 'object') {
    return {};
  }
  
  const entries = Object.entries(cacheObject);
  if (entries.length <= maxSize) {
    return cacheObject;
  }
  
  // Sort by lastFetched (oldest first) and keep only the most recent entries
  const sortedEntries = entries.sort(([, a], [, b]) => {
    const aTime = a.lastFetched || 0;
    const bTime = b.lastFetched || 0;
    return aTime - bTime;
  });
  
  const limitedEntries = sortedEntries.slice(-maxSize);
  return Object.fromEntries(limitedEntries);
}; 